import { Check, Copy, Loader2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { AnalyticsEvents, captureAnalyticsEvent } from "@/analytics";
import { useAuth } from "@/auth/auth";
import { useOrganization } from "@/client/backend/organization";
import { useCreateAPIKey } from "@/client/backend/api-key";
import { OnboardingShell } from "@/components/onboarding/shared";
import { Button } from "@/components/ui/button";

const skillCommand = "npx -y lim skills install";

export function AgentIDOnboarding() {
  const { userTid } = useAuth();
  const [organization] = useOrganization();
  // An account or organization switch must discard the previous credential.
  return <AgentSetup key={`${userTid}:${organization === "loading" ? "loading" : organization.id}`} />;
}

function AgentSetup() {
  const [organization] = useOrganization();
  const { trigger: createToken } = useCreateAPIKey(
    organization === "loading" ? "loading" : organization.id,
  );
  const [apiKey, setApiKey] = useState<string>();
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string>();
  const [copied, setCopied] = useState(false);
  const creatingRef = useRef(false);
  const copyTimeout = useRef<ReturnType<typeof setTimeout>>(undefined);
  useEffect(() => () => clearTimeout(copyTimeout.current), []);

  const createApiKey = async () => {
    if (organization === "loading" || apiKey || creatingRef.current) return;
    creatingRef.current = true;
    setCreating(true);
    setError(undefined);
    try {
      const result = await createToken({ description: "AgentID onboarding CLI key", expirationMonths: 12 });
      if (result?.error || !result?.data?.token) throw new Error("Key creation failed");
      setApiKey(result.data.token);
    } catch {
      setError("Could not create an API key. Try again, or manage keys in Settings.");
    } finally {
      creatingRef.current = false;
      setCreating(false);
    }
  };

  const setupCommands = `${skillCommand} &&\nnpx -y lim set-api-key ${apiKey ?? "<api-key>"}`;
  const copy = async () => {
    if (!apiKey) return;
    try {
      await navigator.clipboard.writeText(setupCommands);
      captureAnalyticsEvent(AnalyticsEvents.ONBOARDING_INSTALL_COMMAND_COPIED, {
        command_type: "terminal_commands", copy_method: "button",
      });
      setError(undefined);
      setCopied(true);
      clearTimeout(copyTimeout.current);
      copyTimeout.current = setTimeout(() => setCopied(false), 2000);
    } catch {
      setError("Could not copy the commands. Select and copy the block below.");
    }
  };

  return (
    <OnboardingShell centered>
      <div className="w-full max-w-2xl min-w-0 py-6">
        <p className="mb-3 text-xs text-muted-foreground">AgentID setup</p>
        <h1 className="text-2xl font-semibold tracking-tight">Set up your Limrun tools</h1>
        <p className="mt-6 text-sm leading-6 text-muted-foreground">
          Use Limrun to build and iterate on Android and iOS apps. You can request remote iOS
          Simulator, Android Emulator as well as remote Xcode and Android builds, including Bazel,
          Expo and all types of mobile projects. It allows you to develop full apps without having to install anything but <code className="text-foreground">lim</code>
          {" "}CLI locally.
        </p>
        <p className="mt-3 text-sm leading-6 text-muted-foreground">
          Install agent skills that teach you how to use Limrun and set the API key to be able
          to connect by running the following commands.
        </p>

        <section className="mt-8" aria-labelledby="agent-setup-heading">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 id="agent-setup-heading" className="text-sm font-semibold">Your setup commands</h2>
            <Button
              size="sm"
              disabled={organization === "loading" || creating}
              onClick={() => void (apiKey ? copy() : createApiKey())}
            >
              {creating ? <Loader2 aria-hidden className="animate-spin" /> : apiKey ? (
                copied ? <Check aria-hidden /> : <Copy aria-hidden />
              ) : null}
              {creating ? "Generating API key..." : apiKey ? (
                copied ? "Copied" : "Copy setup commands"
              ) : "Generate API key"}
            </Button>
          </div>
          <pre className="mt-2 overflow-x-auto rounded-lg border bg-card p-4 text-sm leading-6"><code>{setupCommands}</code></pre>
          <p className="mt-2 text-xs leading-5 text-muted-foreground">
            {organization === "loading" ? "Loading your organization..." : `Your key gives you access to ${organization.displayName}.`}
            {" "}Manage or revoke it in <Link className="underline underline-offset-4" to="/settings">Settings</Link>.
          </p>
        </section>
        {error && <p role="alert" className="mt-4 text-sm text-destructive">{error}</p>}
      </div>
    </OnboardingShell>
  );
}
